import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_BASE = "https://newerp.awasthivitality.com/api";

const EditInvoice = () => {
  const { invoice_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [invoiceData, setInvoiceData] = useState(null);
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0); // Added discount state

  useEffect(() => {
    if (!invoice_id) return;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchInvoiceData(), fetchPatients()]);
      } finally {
        setLoading(false);
      }
    })();
  }, [invoice_id]);

  const fetchInvoiceData = async () => {
    try {
      const res = await fetch(`${API_BASE}/invoices/${invoice_id}/edit`, {
        headers: { Accept: "application/json" },
      });
      const json = await res.json();

      if (json?.success && json?.data) {
        const inv = json.data;
        setInvoiceData(inv);
        setSelectedPatient(inv.patient || null);
        setInvoiceDate(inv.invoice_date || "");
        setDiscount(Number(inv.discount ?? 0)); // Set discount from API

        const formattedItems = (inv.invoice_details || []).map((d) => ({
          description: d.description || "",
          quantity: Number(d.quantity || 0),
          amount: Number(d.amount ?? d.total ?? 0),
        }));
        setItems(formattedItems);

        const formattedProducts = (inv.products || []).map((p) => ({
          id: p.product_id,
          label: p.product_name,
          amount: Number(p.final_price ?? 0),
        }));
        setProducts(formattedProducts);
      } else {
        setInvoiceData(null);
      }
    } catch (e) {
      console.error("Error fetching invoice:", e);
      setInvoiceData(null);
    }
  };

  const fetchPatients = async () => {
    try {
      const res = await fetch(`${API_BASE}/patients/search`, {
        headers: { Accept: "application/json" },
      });
      const json = await res.json();
      setPatients(Array.isArray(json?.data) ? json.data : []);
    } catch (e) {
      console.error("Error fetching patients:", e);
      setPatients([]);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    if (field === "description") updated[index].description = value;
    else if (field === "quantity") updated[index].quantity = Number(value || 0);
    else if (field === "amount") updated[index].amount = Number(value || 0);
    setItems(updated);
  };

  const handleProductSelect = (index, productId) => {
    const option = products.find((o) => String(o.id) === String(productId));
    const updated = [...items];
    if (option) {
      updated[index].description = option.label;
      updated[index].amount = option.amount;
      if (!updated[index].quantity || updated[index].quantity < 1) {
        updated[index].quantity = 1;
      }
    } else {
      updated[index].description = "";
      updated[index].amount = 0;
    }
    setItems(updated);
  };

  const addItem = () => {
    setItems((prev) => [...prev, { description: "", quantity: 1, amount: 0 }]);
  };

  const uiTotal = items.reduce(
    (sum, i) => sum + Number(i.amount || 0) * Number(i.quantity || 0),
    0
  );

  const totalAfterDiscount = Math.max(uiTotal - Number(discount || 0), 0); // Total after discount

  const handleSubmit = async () => {
    if (!selectedPatient) return alert("Please select a patient!");
    const validItems = items.filter(
      (i) =>
        i.description.trim() !== "" &&
        Number(i.quantity) > 0 &&
        Number(i.amount) > 0
    );
    if (validItems.length === 0) return alert("Please add at least one item!");

    const updateData = {
      patient_id: selectedPatient.patient_id,
      invoice_date: invoiceDate,
      discount: Number(discount || 0), // Include discount in API
      products: validItems.map((i) => ({
        description: i.description.trim(),
        quantity: Number(i.quantity || 0),
        amount: Number(i.amount || 0), // Send unit price, backend will multiply by quantity
      })),
    };

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE}/invoices/${invoice_id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(updateData),
      });
      const result = await res.json();
      if (result?.success) {
        const apiTotal = Number(result?.data?.total_amount ?? 0);
        alert(
          `✅ Invoice updated!\nInvoice No: ${
            result?.data?.invoice_no || invoiceData?.invoice_no || ""
          }\nTotal Amount: ₹${(apiTotal > 0 ? apiTotal : totalAfterDiscount).toFixed(2)}`
        );
        await fetchInvoiceData();
        navigate("/InvoiceList");
      } else {
        alert(`❌ Error: ${result?.message || "Update failed"}`);
      }
    } catch (e) {
      console.error("Error updating invoice:", e);
      alert("Failed to update invoice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div>Loading invoice data...</div>;
  if (!invoiceData) return <div>Invoice not found!</div>;

  // Styles
  const styles = {
    container: { maxWidth: 1000, margin: "0 auto", padding: 20 },
    field: { width: "100%", padding: 10, marginBottom: 10 },
    label: { fontWeight: "bold", marginTop: 10, display: "block" },
    cell: { padding: 10, border: "1px solid #ddd" },
    button: {
      padding: "15px 30px",
      backgroundColor: submitting ? "#6c757d" : "#28a745",
      color: "white",
      border: "none",
      borderRadius: 5,
      fontSize: 16,
      cursor: submitting ? "not-allowed" : "pointer",
    },
  };

  return (
    <div style={styles.container}>
      <h2>Edit Invoice - {invoiceData.invoice_no}</h2>

      <div>
        <label htmlFor="patient" style={styles.label}>Patient:</label>
        <select
          id="patient"
          value={selectedPatient?.patient_id || ""}
          onChange={(e) =>
            setSelectedPatient(
              patients.find((p) => String(p.patient_id) === e.target.value) || null
            )
          }
          style={styles.field}
        >
          <option value="">Select Patient</option>
          {patients.map((p) => (
            <option key={p.patient_id} value={p.patient_id}>
              {p.display_text || p.name}
            </option>
          ))}
        </select>

        <label htmlFor="invoiceDate" style={styles.label}>Date:</label>
        <input
          id="invoiceDate"
          type="date"
          value={invoiceDate}
          onChange={(e) => setInvoiceDate(e.target.value)}
          style={styles.field}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          type="button"
          onClick={addItem}
          style={{
            padding: "8px 12px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          + Add Item
        </button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
        <thead>
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            <th style={styles.cell}>S.No.</th>
            <th style={styles.cell}>Product</th>
            <th style={styles.cell}>Quantity</th>
            <th style={styles.cell}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const selected = products.find((p) => p.label === item.description);
            const selectedId = selected ? selected.id : "";
            return (
              <tr key={i}>
                <td style={{ ...styles.cell, textAlign: "center" }}>{i + 1}</td>
                <td style={styles.cell}>
                  <select
                    value={selectedId}
                    onChange={(e) => handleProductSelect(i, e.target.value)}
                    style={{ width: "100%", padding: 8 }}
                  >
                    <option value="">Select Description</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label} - ₹{p.amount}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={styles.cell}>
                  <input
                    type="number"
                    min="0"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                    style={{ width: "100%", padding: 8 }}
                  />
                </td>
                <td style={styles.cell}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={(e) => handleItemChange(i, "amount", e.target.value)}
                    style={{ width: "100%", padding: 8 }}
                  />
                </td>
              </tr>
            );
          })}
          <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
            <td colSpan={3} style={{ ...styles.cell, textAlign: "right" }}>Subtotal:</td>
            <td style={styles.cell}>₹{uiTotal.toFixed(2)}</td>
          </tr>
          <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
            <td colSpan={3} style={{ ...styles.cell, textAlign: "right" }}>
              Discount:
            </td>
            <td style={styles.cell}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value || 0))}
                style={{ width: "100%", padding: 8 }}
              />
            </td>
          </tr>
          <tr style={{ backgroundColor: "#e9ecef", fontWeight: "bold" }}>
            <td colSpan={3} style={{ ...styles.cell, textAlign: "right" }}>Total After Discount:</td>
            <td style={styles.cell}>₹{totalAfterDiscount.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={styles.button}
      >
        {submitting ? "Updating..." : "Update Invoice"}
      </button>
    </div>
  );
};

export default EditInvoice;